// §6.3 / §9.2 — the Windows 11 taskbar.
//
// 48px tall, the icon cluster centred, Start at its left end, tray and clock pinned right. The
// centring is real centring: the cluster is the flex box's only centred child and the tray is
// taken out of flow, so a fourteenth app does not push the clock off the screen or drag the
// cluster off centre.
//
// Entries are grouped **by app**, the way Windows groups them — one icon however many windows an
// app has open, with the running indicator saying which. Right-clicking one opens its §9.2 jump
// list; picking an entry is delivered to the app as §9.3's `command`/`fire` (D-100).
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Win } from '@hangar/shared';
import { LayoutGrid } from '@/assets/icons';
import { COPY } from '@/copy';
import {
  useFocusedWindowId,
  useKernel,
  useNotificationBadges,
  useUnhealthyApps,
  useWindows,
} from '@/kernel/react';
import { SnapAssist } from './SnapAssist';
import { TrayFlyout } from './misc';

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 10_000);
    return () => clearInterval(timer);
  }, []);
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

interface AppGroup {
  appId: string;
  windows: Win[];
}

/** One entry per app, in the order each app's first window was opened. */
function groupByApp(windows: readonly Win[]): AppGroup[] {
  const groups = new Map<string, AppGroup>();
  for (const win of windows) {
    const group = groups.get(win.appId);
    if (group) group.windows.push(win);
    else groups.set(win.appId, { appId: win.appId, windows: [win] });
  }
  return [...groups.values()];
}

interface JumpListState {
  appId: string;
  /** Viewport coordinates of the taskbar button the menu belongs to. */
  x: number;
  y: number;
}

export function Bar() {
  const kernel = useKernel();
  const windows = useWindows();
  const focusedId = useFocusedWindowId();
  const unhealthy = useUnhealthyApps();
  const badges = useNotificationBadges();
  const time = useClock();
  const [jumpList, setJumpList] = useState<JumpListState | null>(null);
  // §6.3 — the tray flyout, which is where a dismissed toast goes to be read again.
  const [tray, setTray] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const trayRef = useRef<HTMLDivElement | null>(null);

  const closeJumpList = useCallback(() => setJumpList(null), []);

  useEffect(() => {
    if (!jumpList) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      closeJumpList();
    };
    const onPointer = (event: PointerEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) closeJumpList();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, [jumpList, closeJumpList]);

  useEffect(() => {
    if (!tray) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setTray(false);
    };
    const onPointer = (event: PointerEvent): void => {
      if (!trayRef.current?.contains(event.target as Node)) setTray(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, [tray]);

  const groups = groupByApp(windows);
  // §5.4 — a Snap Group gets its own entry, and it moves all of its members together.
  const snapGroups = kernel.snap.groupsOn(kernel.wm.getState().activeDesktopId);

  // Clicking an app's icon: focus it, or minimize it if it already has focus, or bring it back.
  const activate = (group: AppGroup): void => {
    const focused = group.windows.find((win) => win.id === focusedId);
    if (focused && focused.state !== 'minimized') {
      kernel.wm.minimize(focused.id);
      return;
    }
    const target = group.windows.find((win) => win.state === 'minimized') ?? group.windows[0];
    if (!target) return;
    if (target.state === 'minimized') kernel.wm.restore(target.id);
    kernel.wm.focus(target.id);
  };

  const openJumpList = (event: React.MouseEvent, appId: string): void => {
    event.preventDefault();
    const button = event.currentTarget.getBoundingClientRect();
    setJumpList({ appId, x: button.x + button.width / 2, y: button.y });
  };

  /** §9.3 `command` — delivered to the app's most recently focused window. */
  const fire = (appId: string, commandId: string): void => {
    closeJumpList();
    const open = windows.filter((win) => win.appId === appId);
    const target = open.find((win) => win.id === focusedId) ?? open[open.length - 1];
    if (target) kernel.apps.fireCommand(target.instanceKey, commandId);
  };

  const closeAll = (appId: string): void => {
    closeJumpList();
    // Each window is asked on its own: an app with one dirty document and three clean ones
    // should close three of them (§18 F9).
    for (const win of windows.filter((w) => w.appId === appId)) void kernel.requestClose(win.id);
  };

  const menuFor = jumpList
    ? { entries: kernel.apps.jumpListOf(jumpList.appId), appId: jumpList.appId }
    : null;

  return (
    <div className="win11-bar" data-testid="bar">
      {/* §5.4 — Snap Assist has no slot in §6.1's closed component set, so it hangs off the one
          always-mounted surface this skin owns above the window layer (D-108). */}
      <SnapAssist />
      <div className="win11-bar-cluster" data-testid="bar-cluster">
        <button
          type="button"
          className="win11-start"
          aria-label={COPY.chrome.start}
          data-testid="bar-launcher"
          onClick={() => kernel.shellui.toggleOverlay('launcher')}
        >
          <LayoutGrid size={20} aria-hidden />
        </button>

        {snapGroups.map((group) => (
          <button
            key={group.groupId}
            type="button"
            className="win11-bar-snapgroup"
            data-testid={`bar-group-${group.layoutId}`}
            data-count={group.windows.length}
            aria-label={COPY.chrome.snapGroup}
            title={COPY.chrome.snappedWindows(group.windows.length)}
            onPointerEnter={() => kernel.snap.restoreGroup(group.groupId)}
            onFocus={() => kernel.snap.restoreGroup(group.groupId)}
            onClick={() => {
              const anyVisible = group.windows.some((win) => {
                const current = kernel.wm.get(win.id);
                return current !== undefined && current.state !== 'minimized';
              });
              if (anyVisible) kernel.snap.minimizeGroup(group.groupId);
              else kernel.snap.restoreGroup(group.groupId);
            }}
          >
            {group.windows.length}
          </button>
        ))}

        {groups.map((group) => {
          const active = group.windows.some(
            (win) => win.id === focusedId && win.state !== 'minimized',
          );
          const visible = group.windows.some((win) => win.state !== 'minimized');
          const first = group.windows[0];
          return (
            <button
              key={group.appId}
              type="button"
              className="win11-bar-entry"
              data-testid={`bar-entry-${group.appId}`}
              // §9.6 — three consecutive misses and the icon dims to 50 %.
              data-health={unhealthy.has(group.appId) ? 'unhealthy' : 'ok'}
              data-window-id={first?.id ?? ''}
              data-active={String(active)}
              data-count={group.windows.length}
              // §13 — "a notification badges its app's bar icon".
              data-badge={badges.get(group.appId) ?? 0}
              title={first?.title ?? group.appId}
              aria-label={first?.title ?? group.appId}
              onClick={() => activate(group)}
              onContextMenu={(event) => openJumpList(event, group.appId)}
            >
              <span
                className="win11-bar-icon"
                aria-hidden
                dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(group.appId) }}
              />
              {/* §6.3's running indicator: a pill that widens when the app has focus, and
                  splits in two when the app has more than one window open. */}
              <span
                className="win11-bar-indicator"
                data-testid={`bar-indicator-${group.appId}`}
                data-state={active ? 'active' : visible ? 'running' : 'minimized'}
                data-many={String(group.windows.length > 1)}
                aria-hidden
              />
              {(badges.get(group.appId) ?? 0) > 0 && (
                <span
                  className="win11-bar-badge"
                  data-testid={`bar-badge-${group.appId}`}
                  aria-label={COPY.chrome.notifications}
                >
                  {badges.get(group.appId)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="win11-bar-tray" data-testid="bar-tray" ref={trayRef}>
        <button
          type="button"
          className="win11-bar-clock"
          data-testid="bar-clock"
          aria-label={COPY.chrome.notifications}
          onClick={() => setTray((shown) => !shown)}
        >
          {time}
        </button>
        {tray ? <TrayFlyout /> : null}
      </div>

      {menuFor ? (
        <div
          ref={menuRef}
          className="win11-jumplist"
          data-testid="bar-jumplist"
          data-app-id={menuFor.appId}
          role="menu"
          style={{ left: jumpList?.x ?? 0, bottom: 'var(--bar-height)' }}
        >
          {menuFor.entries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="menuitem"
              className="win11-jumplist-item"
              data-testid={`bar-jumplist-${entry.id}`}
              onClick={() => fire(menuFor.appId, entry.id)}
            >
              {entry.label}
            </button>
          ))}
          {menuFor.entries.length > 0 ? <hr className="win11-jumplist-rule" /> : null}
          {/* The shell's own entry, on every running app — an app cannot decline to be closed
              from the taskbar, and a jump list is never empty because of it. */}
          <button
            type="button"
            role="menuitem"
            className="win11-jumplist-item"
            data-testid="bar-jumplist-close-all"
            onClick={() => closeAll(menuFor.appId)}
          >
            {COPY.chrome.closeAllWindows}
          </button>
        </div>
      ) : null}
    </div>
  );
}
